import React, { useState } from 'react';
import { Button, Skeleton, Tag, Popconfirm } from 'antd';
import { CopyOutlined, EditOutlined, DeleteOutlined, DownOutlined, BranchesOutlined, MessageOutlined } from '@ant-design/icons';
import StepCard from './StepCard';
import MessageBubble from './MessageBubble';

const SequenceCard = ({
    item,
    onUse,
    onEdit,
    onDelete,
    loadModules,
    isOwner,
    isAdmin,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [modules, setModules] = useState(null);
    const [loadingModules, setLoadingModules] = useState(false);

    const isMultiStep = item.type === 'multi-step';

    const handleExpand = async () => {
        const nextExpanded = !expanded;
        setExpanded(nextExpanded);

        // Lazy-load modules for multi-step items on first expand
        if (nextExpanded && isMultiStep && !modules && loadModules) {
            setLoadingModules(true);
            try {
                const loaded = await loadModules(item.moduleGroupId || item.id);
                setModules(loaded);
            } catch (err) {
                console.error('Failed to load modules:', err);
            }
            setLoadingModules(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleExpand();
        }
    };

    return (
        <div style={{
            background: '#fff',
            border: `1px solid ${expanded ? '#A5D6A7' : '#E6E6E6'}`,
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            transition: 'border-color 0.25s, box-shadow 0.25s',
        }}>
            {/* Card header */}
            <div
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-label={`${item.title} - ${isMultiStep ? 'Sequence' : 'Message'} template, ${item.stepCount} ${item.stepCount === 1 ? 'step' : 'steps'}`}
                onKeyDown={handleKeyDown}
                style={{
                    padding: '16px 20px',
                    cursor: 'pointer',
                    background: expanded ? '#E8F5E9' : 'transparent',
                    transition: 'background 0.2s',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 16,
                    outline: 'none',
                }}
            >
                <div style={{ flex: 1, cursor: 'pointer' }} onClick={handleExpand}>
                    {/* Top badges */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                        <Tag
                            icon={isMultiStep ? <BranchesOutlined /> : <MessageOutlined />}
                            color={isMultiStep ? 'purple' : 'blue'}
                            style={{ margin: 0, borderRadius: 999 }}
                        >
                            {isMultiStep ? 'Sequence' : 'Message'}
                        </Tag>
                        {isMultiStep && (
                            <Tag color="default" style={{ margin: 0, borderRadius: 999 }}>
                                {item.stepCount} {item.stepCount === 1 ? 'step' : 'steps'}
                            </Tag>
                        )}
                        {item.category && (
                            <Tag color="blue" style={{ margin: 0, borderRadius: 999 }}>{item.category}</Tag>
                        )}
                    </div>

                    {/* Title */}
                    <h3 style={{
                        margin: '0 0 4px',
                        fontSize: 16,
                        fontWeight: 600,
                        color: '#263238',
                        lineHeight: 1.3,
                    }}>
                        {item.title}
                    </h3>

                    {/* Description preview when collapsed (sequences only) */}
                    {!expanded && isMultiStep && item.description && (
                        <div style={{ fontSize: 13, color: '#607D8B', marginTop: 4 }}>
                            {item.description.length > 120 ? `${item.description.substring(0, 120)}...` : item.description}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={(e) => { e.stopPropagation(); onUse(item); }}
                        style={{ borderColor: '#4CAF50', color: '#4CAF50' }}
                        aria-label={`Use template: ${item.title}`}
                    >
                        Use
                    </Button>
                    {/* Expand arrow */}
                    <div
                        onClick={handleExpand}
                        role="button"
                        tabIndex={-1}
                        aria-hidden="true"
                        style={{
                            color: expanded ? '#4CAF50' : '#d1d5db',
                            fontSize: 14,
                            flexShrink: 0,
                            transform: expanded ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.25s, color 0.2s',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            padding: 4,
                        }}
                    >
                        <DownOutlined />
                    </div>
                </div>
            </div>

            {/* Expanded content */}
            {expanded && (
                <div style={{ padding: '0 20px 20px', borderTop: '1px solid #E6E6E6' }}>
                    {/* Description (sequences only) */}
                    {isMultiStep && item.description && (
                        <p style={{
                            fontSize: 13.5,
                            color: '#4b5563',
                            lineHeight: 1.65,
                            margin: '16px 0 24px',
                            padding: '12px 16px',
                            background: '#FAFAFA',
                            borderRadius: 6,
                            borderLeft: '3px solid #4CAF50',
                        }}>
                            {item.description}
                        </p>
                    )}

                    {/* Single-step: show message bubble directly */}
                    {!isMultiStep && item.message && (
                        <div style={{ marginTop: 16, marginBottom: 16 }}>
                            <MessageBubble msg={item.message} />
                            {/* Edit/Delete for owned single-step templates */}
                            {(isOwner || isAdmin) && (
                                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                    {onEdit && (
                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => onEdit(item)}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                    {onDelete && (
                                        <Popconfirm
                                            title="Are you sure you want to delete this template?"
                                            onConfirm={() => onDelete(item)}
                                            okText="Yes"
                                            cancelText="No"
                                        >
                                            <Button
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                            >
                                                Delete
                                            </Button>
                                        </Popconfirm>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Multi-step: show step cards */}
                    {isMultiStep && (
                        <div>
                            {loadingModules && (
                                <div style={{ padding: '16px 0' }}>
                                    {[1, 2, 3].map(i => (
                                        <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-start' }}>
                                            <Skeleton.Avatar active size={24} shape="circle" />
                                            <div style={{ flex: 1 }}>
                                                <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 1, width: ['90%'] }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {modules && modules.length > 0 && (
                                <div>
                                    {modules.map((mod, i) => (
                                        <StepCard
                                            key={mod.id}
                                            step={mod}
                                            index={i}
                                            total={modules.length}
                                        />
                                    ))}
                                </div>
                            )}
                            {modules && modules.length === 0 && (
                                <div style={{ color: '#607D8B', fontSize: 13, fontStyle: 'italic' }}>
                                    No steps configured yet.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default React.memo(SequenceCard);
